import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useDispatch, useSelector } from 'react-redux';
import { setPrices, mergePrices, setConnected } from '@/redux/slices/goldPriceSlice';
import type { AppDispatch, RootState } from '@/redux';
import type { GoldPriceDTO } from '@/services/goldPrice.service';

interface BeQuote {
  instrument_code: string;
  bid: number;
  ask: number;
  ts: string;
  override_applied?: boolean;
}

interface SnapshotPayload {
  quotes: BeQuote[];
  ts: string;
}

function mapQuotes(quotes: BeQuote[]): GoldPriceDTO[] {
  return quotes.map((q) => ({
    type: q.instrument_code,
    buy: q.bid,
    sell: q.ask,
    updatedAt: q.ts,
    overrideApplied: q.override_applied,
  }));
}

export function useGoldPriceSocket(): boolean {
  const dispatch = useDispatch<AppDispatch>();
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
  const socketRef = useRef<Socket | null>(null);
  const wsConnectedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;
    const wsUrl = import.meta.env.VITE_WS_URL;
    if (!wsUrl) return;

    if (import.meta.env.DEV) {
      console.warn(
        '[useGoldPriceSocket] connecting to',
        `${wsUrl}/gold-prices`,
        'token prefix:',
        accessToken.slice(0, 4),
      );
    }

    const socket: Socket = io(`${wsUrl}/gold-prices`, {
      transports: ['websocket'],
      auth: { token: accessToken },
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      wsConnectedRef.current = true;
      dispatch(setConnected(true));
      if (import.meta.env.DEV) {
        console.warn('[useGoldPriceSocket] connected');
      }
    });

    socket.on('disconnect', (reason) => {
      wsConnectedRef.current = false;
      dispatch(setConnected(false));
      if (import.meta.env.DEV) {
        console.warn('[useGoldPriceSocket] disconnected:', reason);
      }
    });

    socket.on('gold-prices:snapshot', (payload: SnapshotPayload) => {
      if (Array.isArray(payload?.quotes)) {
        dispatch(setPrices(mapQuotes(payload.quotes)));
      }
    });

    socket.on('gold-prices:updated', (payload: SnapshotPayload) => {
      if (!Array.isArray(payload?.quotes) || payload.quotes.length === 0) return;
      dispatch(mergePrices(mapQuotes(payload.quotes)));
    });

    socket.on('connect_error', (err) => {
      if (import.meta.env.DEV) {
        console.warn('[useGoldPriceSocket] connect_error:', err.message, err);
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      wsConnectedRef.current = false;
    };
  }, [accessToken, isAuthenticated, dispatch]);

  return wsConnectedRef.current;
}
